/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

// Keep GEL X access-gate submissions at the top of the product page.
(() => {
  const SCROLL_KEY = 'kmc:gelx-access-gate:scroll-top';

  const isGateForm = (form) =>
    form instanceof HTMLFormElement && Boolean(form.closest('[data-product-access-gate]'));

  const cleanResponseUrl = () => {
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('contact_posted');
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}`
      );
    } catch (error) {
      console.error('[KMC] Unable to clean the access-gate response URL.', error);
    }
  };

  const forceProductTop = () => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const reset = () => window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    reset();
    window.requestAnimationFrame(() => window.requestAnimationFrame(reset));
    window.setTimeout(reset, 80);
    window.setTimeout(reset, 250);
  };

  const completeSuccessfulReturn = () => {
    const submitted = document.querySelector(
      '[data-product-access-gate] [data-gate-submitted]'
    );

    let pending = false;
    try {
      pending = window.sessionStorage.getItem(SCROLL_KEY) === '1';
    } catch (error) {}

    if (!submitted && !pending) return;

    try {
      window.sessionStorage.removeItem(SCROLL_KEY);
    } catch (error) {}

    cleanResponseUrl();
    forceProductTop();
    window.addEventListener('load', forceProductTop, { once: true });
    window.addEventListener('pageshow', forceProductTop, { once: true });
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!isGateForm(form)) return;

      try {
        window.sessionStorage.setItem(SCROLL_KEY, '1');
      } catch (error) {}

      try {
        const action = new URL(form.action, window.location.href);
        action.hash = '';
        form.action = action.toString();
      } catch (error) {
        console.error('[KMC] Unable to remove the access-gate return anchor.', error);
      }
    },
    true
  );

  completeSuccessfulReturn();
  document.addEventListener('DOMContentLoaded', completeSuccessfulReturn, { once: true });
})();

// GEL X product controls only. The access gate exists only on food-gelx.
(() => {
  const applyGelxControlStyles = () => {
    if (!document.querySelector('[data-product-access-gate]')) return;
    if (document.getElementById('kmc-gelx-product-control-styles')) return;

    const style = document.createElement('style');
    style.id = 'kmc-gelx-product-control-styles';
    style.textContent = `
      .product-form__submit,
      .product-form__submit span {
        font-weight: 700 !important;
      }

      .quantity__input,
      .quantity__button,
      .quantity__button svg {
        color: #000 !important;
        opacity: 1 !important;
      }

      .quantity__button svg path {
        fill: currentColor !important;
      }
    `;
    document.head.appendChild(style);
  };

  applyGelxControlStyles();
  document.addEventListener('DOMContentLoaded', applyGelxControlStyles, { once: true });
})();
